import type { FC } from 'react'
import { Skeleton, Image } from 'antd'
import {
  RotateLeftOutlined,
  RotateRightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'

import { useAppStore } from '@/store'
import { rqKeys } from '@/react-query-keys'
import { formatMoney, formatTs } from '@/helpers'
import { Drawer } from '@/components'
import { E_CheckStatus } from '@/enums'
import {
  SC_List,
  SC_CheckItem,
  SC_CheckHeader,
  SC_Date,
  SC_Amount,
  SC_Status,
  SC_RejectReason,
  SC_ImageWrapper,
} from './styled'


interface T_ChecksHistoryDrawerProps {
  onClose: () => void
}

interface T_Check {
  id: number
  amount: number
  status: E_CheckStatus
  rejectMessage: string | null
  createdAt: string
  image: string
}

export const ChecksHistoryDrawer: FC<T_ChecksHistoryDrawerProps> = ({
  onClose,
}) => {
  const userData = useAppStore((s) => s.userData)

  const {
    data: checks = [],
    isLoading,
  } = useQuery<T_Check[]>({
    queryKey: [ rqKeys.waitingChecks, userData.id ],
    queryFn: async () => {
      if (!userData.id) return []
      // AUX_API_URL removed - returning empty array
      return []
    },
    enabled: !!userData.id,
  })


  return (
    <Drawer
      onClose={onClose}
      heading='Ваши запросы'
    >
      {isLoading ? (
        <Skeleton active />
      ) : (
        <SC_List>
          {checks.map((check) => (
            <SC_CheckItem key={check.id}>
              <SC_CheckHeader>
                <SC_Date>{formatTs(new Date(check.createdAt).getTime())}</SC_Date>

                <SC_Status $status={check.status}>
                  {check.status === E_CheckStatus.NEW ? 'В обработке' : 'Отклонен'}
                </SC_Status>
              </SC_CheckHeader>

              <SC_Amount>{formatMoney(check.amount)}</SC_Amount>

              {check.image && (
                <SC_ImageWrapper>
                  <Image
                    src={check.image.replace('checks/', '')} // AUX_API_URL removed
                    alt='Check'
                    preview={{
                      toolbarRender: (
                        _,
                        {
                          transform: { scale },
                          actions: { onRotateLeft, onRotateRight, onZoomOut, onZoomIn },
                        },
                      ) => (
                        <div style={{
                          display: 'flex',
                          gap: '24px',
                          justifyContent: 'center',
                          alignItems: 'center',
                          color: 'white',
                          fontSize: '20px',
                          background: 'rgba(0, 0, 0, 0.5)',
                          padding: '8px 24px',
                          borderRadius: '100px',
                          position: 'fixed',
                          bottom: '50px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          zIndex: 1000,
                        }}>
                          <RotateLeftOutlined onClick={onRotateLeft} style={{ cursor: 'pointer' }} />
                          <RotateRightOutlined onClick={onRotateRight} style={{ cursor: 'pointer' }} />
                          <ZoomOutOutlined disabled={scale === 1} onClick={onZoomOut} style={{ cursor: scale === 1 ? 'not-allowed' : 'pointer', opacity: scale === 1 ? 0.5 : 1 }} />
                          <ZoomInOutlined disabled={scale === 50} onClick={onZoomIn} style={{ cursor: scale === 50 ? 'not-allowed' : 'pointer', opacity: scale === 50 ? 0.5 : 1 }} />
                        </div>
                      ),
                    }}
                  />
                </SC_ImageWrapper>
              )}

              {check.status === E_CheckStatus.REJECTED && check.rejectMessage && (
                <SC_RejectReason>
                  Причина: {check.rejectMessage}
                </SC_RejectReason>
              )}
            </SC_CheckItem>
          ))}
        </SC_List>
      )}
    </Drawer>
  )
}
